import { useEffect } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useResourceAnalysisStore } from 'store/resourceAnalysis.store';
import { useThemeStatusStore } from 'store/themeStatus.store';
import { useVersionStore } from 'store/version.store';

import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { UseResourcesAnalysisChartProvider } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.provider';
import {
    categoriesKeys,
    getChartConfig,
    serialize,
} from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.utils';

interface Props {
    ui: UseResourcesAnalysisChartProvider['ui'];
    api: UseResourcesAnalysisChartProvider['api'];
    title: string;
    subTitle: string;
    latestVersionId: number | undefined;
    widgetViewMode: WidgetViewMode;
    setIsPayloadReady?: (newValue: boolean) => void;
}

const CHART_HEIGHT_IN_REPORT_VIEW = '100%';
const CHART_HEIGHT_IN_PAGE_VIEW = 600;

export const useResourcesAnalysisChartLogic = ({
    ui,
    api,
    title,
    subTitle,
    latestVersionId,
    widgetViewMode,
    setIsPayloadReady,
}: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.getResourceChart.payload;
        if (!currentPayload) return;
        if (currentPayload.lastMetaDataId === undefined || currentPayload.comparedMetaDataId === undefined) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    const { themeStatus } = useThemeStatusStore();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { version } = useVersionStore();
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    const { dataPointIndex: dataPointIndexFromStore, seriesIndex: seriesIndexFromStore } = useResourceAnalysisStore();

    const dataPointSelection = ({ dataPointIndex, seriesIndex }: { dataPointIndex: number; seriesIndex: number }) => {
        const isToggle = dataPointIndex === dataPointIndexFromStore && seriesIndex === seriesIndexFromStore;
        const series = categoriesKeys[seriesIndex].label;
        const value = api.getResourceChart.instance.data?.chart[dataPointIndex][categoriesKeys[seriesIndex].series];
        ui.setters.updateResourcesAnalysisStore({
            selectedValue: isToggle ? null : value,
            selectedSeries: isToggle ? null : series,
            selectedStartDate: isToggle
                ? api.getResourceChart.instance.data?.startChartDate
                : api.getResourceChart.instance.data?.chart[dataPointIndex]?.startDate,
            selectedEndDate: isToggle
                ? api.getResourceChart.instance.data?.endChartDate
                : api.getResourceChart.instance.data?.chart[dataPointIndex]?.endDate,
            dataPointIndex: isToggle ? null : dataPointIndex,
            seriesIndex: isToggle ? null : seriesIndex,
        });
    };

    const tooltipFormatter = ({ dataPointIndex }: { dataPointIndex: number }) => {
        if (dataPointIndex === undefined || !api.getResourceChart.instance.data) return '';
        const { startDate, endDate } = api.getResourceChart.instance.data.chart[dataPointIndex];
        return `${moment(startDate).format(constants.formats.date.default)} - ${moment(endDate).format(constants.formats.date.default)}`;
    };

    const { options, series } = getChartConfig({
        ...serialize({
            data: api.getResourceChart.instance.data?.chart,
            range: ui.state?.dateRangeFilter,
        }),
        themeStatus,
        versionDate: version?.versionDate,
        calculatedVersionDate: api.getResourceChart.instance.data?.calculatedVersionDate,
        dataPointSelection,
        tooltipFormatter,
        actualKpi: api.getResourceChart.instance.data?.actualKpi.activities,
    });

    const handleCsvExport = () => {
        if (latestVersionId) {
            api.exportToCsvResourceChartBar.instance.mutate(
                { ...api.exportToCsvResourceChartBar.payload, fileName: `${title}_${subTitle}` },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'CSV',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    useEffect(() => {
        if (api.getResourceChart.instance.data) {
            ui.setters.updateResourcesAnalysisStore({
                selectedStartDate: api.getResourceChart.instance.data.startChartDate,
                selectedEndDate: api.getResourceChart.instance.data.endChartDate,
            });
        }
    }, [api.getResourceChart.instance.data]);

    const chartHeight = WidgetViewModeUtils.isReportView(widgetViewMode)
        ? CHART_HEIGHT_IN_REPORT_VIEW
        : CHART_HEIGHT_IN_PAGE_VIEW;

    return {
        logic: {
            options,
            series,
            handleCsvExport,
            chartHeight,
        },
    };
};
